/**
 * Custom hook untuk mengambil detail booking berdasarkan ID.
 * 
 * API Endpoint: GET `/api/bookings/:id`
 * API Version: 3.0
 * 
 * @param {string} id - ID Rate Plan
 * @param {string} roomTypeId - ID Tipe Kamar
 * @param {string} parentId - ID Parent Rate Plan (if use this it will be show all linked rate plan with this parent id)
 * @param {string} name - Nama Rate Plan
 * @param {string} include - Include data (e.g: roomType,children)
 * @param {string} orderBy - Order by (e.g: name,created_at)
 * @param {string} sort - Sort (e.g: asc,desc)
 * @param {number} limit - Limit data
 * @param {boolean} all - Get all data (if use this it will be show all rate plan without pagination)
 * @returns {object} - Return value berupa object dengan key `data`, `isLoading`, dan `error`.
 * @example
 * const { data, isLoading, error } = useIndexRatePlan({ id: 'rate-plan-id' });
 * const { data, isLoading, error } = useIndexRatePlan({ roomTypeId: 'room-type-id' });
 * const { data, isLoading, error } = useIndexRatePlan({ parentId: 'parent-id' });
 * const { data, isLoading, error } = useIndexRatePlan({ name: 'rate-plan-name' });
 * const { data, isLoading, error } = useIndexRatePlan({ include: 'roomType,children' });
 */


import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { IndexRatePlanSchema } from "../response/IndexRatePlanResponse";

interface IndexRatePlanProps {
    id?: string;
    roomTypeId?: string;
    parentId?: string;
    name?: string;
    include?: string;
    orderBy?: string;
    sort?: string;
    limit?: number;
    all?: boolean;
}

const useIndexRatePlan = ({ id, roomTypeId, parentId, name, include, orderBy, sort, limit, all }: IndexRatePlanProps) => {
    const filterParams = {
        ...(id && { id }),
        ...(roomTypeId && { room_type_id: roomTypeId }),
        ...(parentId && { parent_id: parentId }),
        ...(name && { name }),
    };

    const params = {
        ...(Object.keys(filterParams).length > 0 && { filter: filterParams }),
        ...(include && { include }),
        ...(orderBy && { order_by: orderBy }),
        ...(sort && { sort }),
        ...(limit && { limit }),
        ...(all && { all: 1 }),
    };

    return useQuery({
        queryKey: ['rooms', 'rates', 'index', id, roomTypeId, parentId, name, include, orderBy, sort, limit, all],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/v4/rate-plan`, { params });

                // Validasi response
                const validationResult = IndexRatePlanSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Rateplan data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Rateplan`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useIndexRatePlan;